import { Badge, Box, Drawer, Flex, Separator, SimpleGrid, Tabs, Text } from '@chakra-ui/react';
import { createDataCollectionContext, dbTimeToUserTime, Loading } from '@shared';
import { useTranslation } from 'react-i18next';
import contactImportRowsService from '../../../../api/services/contactImportRowsService';
import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useGetSingleContactImport } from '../../../../api/hooks/useContactImports';
import { useMeta } from '../../../../api/hooks/useMeta';
import { useGetAllGroups } from '@admin';

const columnHelper = createColumnHelper<ContactImportRow>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function ImportDrawer({ importID }: { importID: number }) {
	const { t } = useTranslation();
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<ContactImportRow>(),
		[],
	);
	const { data: groupData } = useGetAllGroups();
	const { data: contactImport, isFetching: isFetchingContactImport } = useGetSingleContactImport(importID, {
		scopeWith: 'successCount|skippedCount|failedCount',
	});
	const { meta } = useMeta('contactImportsService');

	function getStatusTranslation(status: string): string {
		switch (status) {
			case 'skipped':
				return t('general.skipped');
			case 'completed':
				return t('general.completed');
			case 'error':
				return t('general.error');
		}

		return status;
	}

	const columns = [
		columnHelper.accessor('importStatus', {
			header: t('general.status'),
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'completed', label: t('general.completed') ?? '' },
					{ value: 'skipped', label: t('general.skipped') ?? '' },
					{ value: 'error', label: t('general.error') ?? '' },
				] as Array<{ value: ImportStatus; label: string }>,
			},
			cell: ({ row }) => {
				return getStatusTranslation(row.original.importStatus);
			},
		}),
		columnHelper.accessor('statusMessage', {
			header: t('general.import_status_message'),
			enableSorting: true,
		}),
		columnHelper.accessor('lineNumber', {
			header: t('general.line_number'),
			enableSorting: true,
		}),
		columnHelper.display({
			header: t('general.phone'),
			enableSorting: true,
			cell: ({ row }) => {
				return (row.original.dataRow as unknown as Record<string, unknown>)['contacts.phone'] || '';
			},
		}),
		columnHelper.display({
			header: t('general.first_name'),
			enableSorting: true,
			cell: ({ row }) => {
				return (row.original.dataRow as unknown as Record<string, unknown>)['contacts.firstName'] || '';
			},
		}),
		columnHelper.display({
			header: t('general.last_name'),
			enableSorting: true,
			cell: ({ row }) => {
				return (row.original.dataRow as unknown as Record<string, unknown>)['contacts.lastName'] || '';
			},
		}),
		columnHelper.display({
			header: t('general.email'),
			enableSorting: true,
			cell: ({ row }) => {
				return (row.original.dataRow as unknown as Record<string, unknown>)['contacts.email'] || '';
			},
		}),
		columnHelper.accessor('timeImported', {
			header: t('general.time_imported'),
			enableSorting: true,
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeImported) {
					return '-';
				}
				return dbTimeToUserTime(row.original.timeImported, TIMEZONE);
			},
		}),
	];

	if (isFetchingContactImport) {
		return <Loading />;
	}

	if (!contactImport) {
		throw new Error('Could not fetch contact import.');
	}

	return (
		<Flex flexDir={'column'} height="100%">
			<Drawer.Header>
				<Drawer.Title marginRight="20px" wordBreak={'break-word'}>
					{t('general.import_details')}
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body flex={1} display={'flex'} flexDir={'column'}>
				<Tabs.Root
					flexDir={'column'}
					defaultValue={'basicDetails'}
					variant={'line'}
					size="sm"
					colorPalette={'brand.primary'}
					width={'100%'}
					lazyMount
					unmountOnExit
					display={'flex'}
					flexDirection={'column'}
					height={'100%'}
					overflow="auto"
				>
					<Tabs.List padding={0} flexWrap={'wrap'}>
						<Tabs.Trigger value="basicDetails" fontWeight={500}>
							{t('general.basic_details')}
						</Tabs.Trigger>

						<Tabs.Trigger value="importRows" fontWeight={500}>
							{t('general.import_rows')}
						</Tabs.Trigger>
					</Tabs.List>
					<Tabs.Content value="basicDetails" display={'flex'} flexDir={'column'} flex="1">
						<SimpleGrid columns={2} gap={4}>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('general.name')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{contactImport?.name}
								</Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('general.status')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{meta?.importStatus?.[contactImport.importStatus]}
								</Text>
							</Box>

							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('general.add_to_groups')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{groupData?.data.groups
										.filter(g => contactImport?.addToGroupIDs?.map(id => parseInt(id)).includes(g.groupID))
										.map(g => g.name)
										.join(', ') || '-'}
								</Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('contacts.default_country_code')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{contactImport?.defaultPhoneCountry}
								</Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('contacts.is_first_row_header')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{t(`general.${contactImport?.isFirstRowHeader}`)}
								</Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('contacts.is_importing_new_only')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{t(`general.${contactImport?.isImportingNewOnly}`)}
								</Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('general.original_file_name')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{contactImport?.originalFileName}
								</Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('contacts.property_updating_method')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{meta?.propertyUpdatingMethod?.[contactImport?.propertyUpdatingMethod]}
								</Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('general.time_created')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{dbTimeToUserTime(contactImport?.timeCreated, TIMEZONE)}
								</Text>
							</Box>
							<Separator gridColumn="1 / -1" />
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('general.mapping_fields')}
								</Text>

								<Box>
									{Object.entries(contactImport.columnMappings).map(([key, value]) => {
										return (
											<Text key={value} userSelect="text" color="gray.600">
												{`${key}: ${value}`}
											</Text>
										);
									})}
								</Box>
							</Box>
							<Separator gridColumn="1 / -1" />
							<Box>
								<Badge backgroundColor="green">
									<Text fontWeight="semibold" color="white">
										{t('general.successful_imports')}
									</Text>
								</Badge>
								<Text userSelect="text" color="gray.600">
									{contactImport?.successCount}
								</Text>
							</Box>
							<Box>
								<Badge backgroundColor="yellow.500">
									<Text fontWeight="semibold" color="white">
										{t('general.skipped_imports')}
									</Text>
								</Badge>
								<Text userSelect="text" color="gray.600">
									{contactImport?.skippedCount}
								</Text>
							</Box>
							<Box>
								<Badge backgroundColor="red.500">
									<Text fontWeight="semibold" color="white">
										{t('general.failed_imports')}
									</Text>
								</Badge>
								<Text userSelect="text" color="gray.600">
									{contactImport?.failedCount}
								</Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700">
									{t('general.error_message')}
								</Text>
								<Text userSelect="text" color="gray.600">
									{contactImport?.errorMessage || '-'}
								</Text>
							</Box>
						</SimpleGrid>
					</Tabs.Content>
					<Tabs.Content value="importRows" display={'flex'} flexDir={'column'} flex="1">
						<DataCollectionContextProvider
							customQueryKey={`contactImportRows-${importID}`}
							dataKey="contactImportRows"
							keyAttribute="contactImportRowID"
							emptyDataText={t('general.no_import_rows')}
							fetchFn={(...args) => contactImportRowsService.getAllByImportId(importID, ...args)}
							initialItemsPerPage={25}
							columns={columns}
							defaultHiddenColumns={[]}
							isServerSide={true}
							isSelectable={false}
							defaultSort={{ id: 'timeCreated', desc: true }}
						>
							<Flex gap={2} flexWrap="wrap" flexShrink={0}>
								<DataCollectionTableHeader />
							</Flex>
							<Box flex={1} overflow="auto" minH={0}>
								<DataCollectionTable />
							</Box>
							<Flex gap={2} flexShrink={0}>
								<DataCollectionTableFooter />
							</Flex>
						</DataCollectionContextProvider>
					</Tabs.Content>
				</Tabs.Root>
			</Drawer.Body>
		</Flex>
	);
}
